import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ShoppingCartSimple, Trash } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { ChinaQuoteLink } from "@/components/ChinaQuoteLink";
import { variantAutoPrice } from "@/lib/cartPricing";

const TABLE_COLUMN_COUNT = 13;

function buildCartRows({ items, coefficient, pricePerSkid, showGtSku, selectedVariants, onToggleVariant, onRemove, onVariantFieldBlur }) {
  const rows = [];
  let lastGroupKey = null;

  items.forEach((item) => {
    const category = (item.category || "").trim();
    const subCategory = (item.sub_category || "").trim();
    const groupKey = category + "::" + subCategory;
    if (groupKey !== lastGroupKey) {
      rows.push(
        <tr key={"group-" + groupKey + "-" + item.id} data-testid={`cart-group-header-${item.id}`}>
          <td
            colSpan={showGtSku ? TABLE_COLUMN_COUNT + 1 : TABLE_COLUMN_COUNT}
            className="border-b border-zinc-200 bg-zinc-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600"
          >
            {category || "Uncategorized"}
            {subCategory ? " / " + subCategory : ""}
          </td>
        </tr>
      );
      lastGroupKey = groupKey;
    }

    const src = `${API}/cart/items/${item.id}/photo`;
    const variants = item.variants && item.variants.length > 0 ? item.variants : [{}];
    variants.forEach((v, idx) => {
      rows.push(
        <tr
          key={`${item.id}-${idx}`}
          className="border-b border-zinc-200 last:border-0"
          data-testid={`cart-row-${item.id}-${idx}`}
        >
          <td className="px-2 py-1.5">
            {v.variant_record_id && (
              <Checkbox
                checked={selectedVariants.has(`${item.id}::${v.variant_record_id}`)}
                onCheckedChange={() => onToggleVariant(item.id, v.variant_record_id)}
                data-testid={`cart-select-variant-checkbox-${item.id}-${idx}`}
              />
            )}
          </td>
          {idx === 0 && (
            <>
              <td className="px-2 py-1.5" rowSpan={variants.length}>
                <div className="group relative inline-block">
                  <img
                    src={src}
                    alt="thumbnail"
                    className="relative h-12 w-12 cursor-zoom-in rounded-sm border border-zinc-300 bg-zinc-50 object-contain transition-transform duration-200 ease-out group-hover:z-50 group-hover:scale-[3] group-hover:border-blue-400 group-hover:shadow-2xl"
                    data-testid={`cart-item-photo-${item.id}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              </td>
              <td className="px-2 py-1.5 max-w-xs text-sm" rowSpan={variants.length}>
                {item.description || "-"}
              </td>
            </>
          )}
          {showGtSku && (
            <td className="px-2 py-1.5 text-sm font-mono-data" data-testid={`cart-gt-sku-${item.id}-${idx}`}>
              {v.gt_sku || "-"}
            </td>
          )}
          {idx === 0 && (
            <td className="px-2 py-1.5 text-sm" rowSpan={variants.length}>
              <ChinaQuoteLink itemNo={item.item_no} />
              {item.blocked && (
                <span
                  className="ml-1.5 rounded-sm bg-red-600 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-white"
                  data-testid={`cart-item-blocked-badge-${item.id}`}
                >
                  Blocked
                </span>
              )}
            </td>
          )}
          <td className="px-2 py-1.5 text-sm">{v.size || "-"}</td>
          <td className="px-2 py-1.5 text-right font-mono-data text-sm">{v.packing_qty ?? "-"}</td>
          <td className="px-2 py-1.5 text-right font-mono-data text-sm">
            {v.inventory_qty != null ? Math.round(v.inventory_qty) : "-"}
          </td>
          <td className="px-2 py-1.5 text-right font-mono-data text-sm font-semibold text-blue-600">
            {variantAutoPrice(v, coefficient, pricePerSkid).toFixed(2)}
          </td>
          <td className="px-2 py-1.5 text-right">
            <Input
              type="number"
              step="0.01"
              defaultValue={v.final_price != null ? v.final_price : variantAutoPrice(v, coefficient, pricePerSkid).toFixed(2)}
              onBlur={(e) => onVariantFieldBlur(item.id, v.variant_record_id, "final_price", e, v.final_price)}
              className="w-24 rounded-sm border-zinc-300 text-right font-mono-data text-sm"
              data-testid={`cart-final-price-input-${item.id}-${idx}`}
            />
          </td>
          <td className="px-2 py-1.5 text-right">
            <Input
              type="number"
              step="1"
              defaultValue={v.carton_qty ?? 0}
              onBlur={(e) => onVariantFieldBlur(item.id, v.variant_record_id, "carton_qty", e, v.carton_qty ?? 0)}
              className="w-20 rounded-sm border-zinc-300 text-right font-mono-data text-sm"
              data-testid={`cart-carton-qty-input-${item.id}-${idx}`}
            />
          </td>
          <td className="px-2 py-1.5 text-right font-mono-data text-sm" data-testid={`cart-total-qty-${item.id}-${idx}`}>
            {(v.carton_qty || 0) * (v.packing_qty || 0)}
          </td>
          <td
            className="px-2 py-1.5 text-right font-mono-data text-sm font-semibold text-emerald-600"
            data-testid={`cart-total-amount-${item.id}-${idx}`}
          >
            {(
              (v.carton_qty || 0) *
              (v.packing_qty || 0) *
              (v.final_price != null ? v.final_price : variantAutoPrice(v, coefficient, pricePerSkid))
            ).toFixed(2)}
          </td>
          {idx === 0 && (
            <td className="px-2 py-1.5 text-right" rowSpan={variants.length}>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => onRemove(item.id)}
                data-testid={`cart-remove-button-${item.id}`}
              >
                <Trash size={14} />
              </Button>
            </td>
          )}
        </tr>
      );
    });
  });

  return rows;
}

export function CartItemsTable({
  items,
  coefficient,
  pricePerSkid = 0,
  showGtSku = false,
  selectedVariants,
  allVariantsSelected,
  onToggleSelectAllVariants,
  onToggleVariant,
  onRemove,
  onVariantFieldBlur,
}) {
  if (items.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-zinc-400" data-testid="cart-empty-state">
        <ShoppingCartSimple size={32} />
        <p className="text-sm">Your cart is empty. Add products from the main list.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-sm border border-zinc-300" data-testid="cart-table">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="bg-zinc-50">
          <tr className="border-b border-zinc-300">
            <th className="px-2 py-2 text-left">
              <Checkbox
                checked={allVariantsSelected}
                onCheckedChange={onToggleSelectAllVariants}
                data-testid="cart-select-all-checkbox"
              />
            </th>
            <th className="px-2 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Photo</th>
            <th className="px-2 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Description</th>
            {showGtSku && (
              <th className="px-2 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">GT SKU</th>
            )}
            <th className="px-2 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Item No</th>
            <th className="px-2 py-2 text-left text-xs uppercase tracking-[0.05em] text-zinc-500">Size</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Pcs/CTN</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">QTY</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Price</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Final Price</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Carton Qty</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Total Qty</th>
            <th className="px-2 py-2 text-right text-xs uppercase tracking-[0.05em] text-zinc-500">Total Amount</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {buildCartRows({ items, coefficient, pricePerSkid, showGtSku, selectedVariants, onToggleVariant, onRemove, onVariantFieldBlur })}
        </tbody>
      </table>
    </div>
  );
}
